import { useState, useEffect } from 'react'
import {
  createFileRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router'
import { toast } from 'sonner'
import { HugeiconsIcon } from '@hugeicons/react'
import { Add01Icon } from '@hugeicons/core-free-icons'

import {
  useAdminOrganizationDetail,
  useAdminAddUserToOrganization,
  useAdminAddUserRole,
  useAdminAllUsers,
} from '@/features/admin/services/admin.convex'
import { AuthGuard } from '@/features/auth'
import { useFormDialog } from '@/contexts/form-dialog-context'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from '@/components/ui/multi-select'
import type { AnyFieldApi } from '@tanstack/react-form'

const ORG_ROLES = [
  'admin',
  'manager',
  'member',
  'analytics',
  'billing',
] as const

export const Route = createFileRoute(
  '/(dashboard)/admin/organizations/$organizationId',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const { organizationId } = Route.useParams()
  const navigate = useNavigate()
  const { location } = useRouterState()
  const { showFormDialog } = useFormDialog()

  const { data: org, isLoading } = useAdminOrganizationDetail(organizationId)
  const { data: allUsers } = useAdminAllUsers()
  const addUser = useAdminAddUserToOrganization()
  const addRole = useAdminAddUserRole()

  function getTabFromRoute(pathname: string): string {
    if (pathname.includes('/billing')) return 'billing'
    if (pathname.includes('/settings')) return 'settings'
    return 'members'
  }

  const [tab, setTab] = useState(() => getTabFromRoute(location.pathname))

  useEffect(() => {
    setTab(getTabFromRoute(location.pathname))
  }, [location.pathname])

  function changeTab(newTab: string) {
    setTab(newTab)
    navigate({
      to: `/admin/organizations/${organizationId}/${newTab}`,
      search: { name: org?.name ?? undefined },
    })
  }

  function handleAddMember() {
    const memberIds = new Set((org?.members ?? []).map((m) => m!.id))
    const eligibleUsers = allUsers.filter((u) => !memberIds.has(u.id))

    showFormDialog<{ userId: string; roleKeys: string[] }, void>({
      title: 'Add Member',
      description: 'Add an existing user to this organization.',
      submitText: 'Add Member',
      showSuccessState: false,
      formOptions: {
        defaultValues: { userId: '', roleKeys: ['member'] },
      },
      isSubmitDisabled: (form) =>
        !form.state.values.userId || form.state.values.roleKeys.length === 0,
      onSubmit: async (data) => {
        const [firstRole, ...extraRoles] = data.roleKeys
        await addUser({
          userId: data.userId as never,
          organizationId: organizationId as never,
          roleKey: firstRole,
        })
        await Promise.all(
          extraRoles.map((k) =>
            addRole({
              userId: data.userId as never,
              organizationId: organizationId as never,
              roleKey: k,
            }),
          ),
        )
        toast.success('User added to organization')
      },
      children: (form) => (
        <div className="space-y-4">
          <form.Field name="userId">
            {(field: AnyFieldApi) => (
              <div className="space-y-1.5">
                <Label htmlFor="add-member-user">User</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) => field.handleChange(v)}
                >
                  <SelectTrigger id="add-member-user" className="w-full">
                    <SelectValue placeholder="Select a user…" />
                  </SelectTrigger>
                  <SelectContent>
                    {eligibleUsers.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name ?? u.email ?? u.id}
                        {u.email && u.name ? ` (${u.email})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>
          <form.Field name="roleKeys">
            {(field: AnyFieldApi) => (
              <div className="space-y-1.5">
                <Label>Roles</Label>
                <MultiSelect
                  values={field.state.value}
                  onValuesChange={(v) => field.handleChange(v)}
                >
                  <MultiSelectTrigger className="w-full">
                    <MultiSelectValue placeholder="Select roles…" />
                  </MultiSelectTrigger>
                  <MultiSelectContent>
                    <MultiSelectGroup>
                      {ORG_ROLES.map((r) => (
                        <MultiSelectItem key={r} value={r}>
                          {r.charAt(0).toUpperCase() + r.slice(1)}
                        </MultiSelectItem>
                      ))}
                    </MultiSelectGroup>
                  </MultiSelectContent>
                </MultiSelect>
              </div>
            )}
          </form.Field>
        </div>
      ),
    })
  }

  if (isLoading) {
    return (
      <AuthGuard requiresPlatformAdmin>
        <div className="space-y-6">
          <div className="h-24 bg-muted animate-pulse rounded-lg" />
          <div className="h-64 bg-muted animate-pulse rounded-lg" />
        </div>
      </AuthGuard>
    )
  }

  if (!org) {
    return (
      <AuthGuard requiresPlatformAdmin>
        <div className="p-6 text-center text-muted-foreground">
          Organization not found.
        </div>
      </AuthGuard>
    )
  }

  return (
    <AuthGuard requiresPlatformAdmin>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between p-6 bg-muted/30 rounded-lg">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold">{org.name}</h1>
              <Badge variant={org.isAdmin ? 'default' : 'secondary'}>
                {org.isAdmin ? 'Platform' : 'Customer'}
              </Badge>
            </div>
            {org.description && (
              <p className="text-sm text-muted-foreground mt-0.5">
                {org.description}
              </p>
            )}
            <p className="text-xs text-muted-foreground font-mono mt-1">
              ID: {org.id}
            </p>
          </div>
          <Button onClick={handleAddMember}>
            <HugeiconsIcon icon={Add01Icon} className="mr-2 h-4 w-4" />
            Add Member
          </Button>
        </div>

        <Tabs value={tab} onValueChange={changeTab}>
          <TabsList>
            <TabsTrigger value="members">
              Members ({org.members.length})
            </TabsTrigger>
            {!org.isAdmin && <TabsTrigger value="billing">Billing</TabsTrigger>}
            {!org.isAdmin && (
              <TabsTrigger value="settings">Settings</TabsTrigger>
            )}
          </TabsList>
          <div className="mt-4">
            <Outlet />
          </div>
        </Tabs>
      </div>
    </AuthGuard>
  )
}
